import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useState } from 'react';

import { IndirectTaxTransactionHelper } from '../helpers';
import { TIndirectTaxTransaction, TIndirectTaxTransactionStatus } from '../types';

interface IndirectTaxTransactionActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: TIndirectTaxTransaction;
  action: TIndirectTaxTransactionStatus | null;
  onConfirm: () => Promise<void>;
}

const ACTION_CONFIG = IndirectTaxTransactionHelper.ACTION_MODAL_CONFIG;

const ACTION_ICON_CONFIG: Record<
  TIndirectTaxTransactionStatus,
  { bgClass: string; icon: JSX.Element }
> = {
  [TIndirectTaxTransactionStatus.VOIDED]: {
    bgClass: 'bg-bg-error',
    icon: <Icons.AlertCircle className="size-5 text-text-error" />,
  },
  [TIndirectTaxTransactionStatus.COMMITTED]: {
    bgClass: 'bg-bg-success',
    icon: <Icons.CheckCircle className="size-5 text-text-success" />,
  },
  [TIndirectTaxTransactionStatus.LOCKED]: {
    bgClass: 'bg-utility-blue-50',
    icon: <Icons.Lock01 className="size-5 text-utility-blue-500" />,
  },
  [TIndirectTaxTransactionStatus.UNCOMMITTED]: {
    bgClass: '',
    icon: <></>,
  },
};

const IndirectTaxTransactionActionModal = ({
  isOpen,
  onClose,
  transaction,
  action,
  onConfirm,
}: IndirectTaxTransactionActionModalProps) => {
  const [isActioning, setIsActioning] = useState(false);

  if (!action) return null;

  const config = ACTION_CONFIG[action];
  const iconConfig = ACTION_ICON_CONFIG[action];

  const handleConfirm = async () => {
    setIsActioning(true);
    await onConfirm();
    setIsActioning(false);
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="min-w-[500px] p-none"
        hideCloseIcon
        preventCloseOnOutsideClick
      >
        <DialogHeader className="pb-xxs border-none">
          <div className="flex flex-col gap-lg">
            <div
              className={cn(
                'flex items-center justify-center size-12 rounded-lg',
                iconConfig.bgClass
              )}
            >
              {iconConfig.icon}
            </div>
            <div className="flex flex-col gap-xs">
              <DialogTitle>{config.title}</DialogTitle>
              <p className="txt-small-regular text-text-tertiary mb-xl">
                {config.description}
              </p>
            </div>
          </div>
        </DialogHeader>

        <DialogFooter className='border-none'>
          <div className="flex gap-md items-center justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={isActioning}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant={config.buttonVariant}
              onClick={handleConfirm}
              disabled={isActioning}
            >
              {config.buttonText}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { IndirectTaxTransactionActionModal };
